import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Check, X, ChevronLeft, ChevronRight, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { hasHandicap, eventHandicap, handicapBaseline, handicapStrokes, handicapLabel } from '../utils/handicap';
import { ScoreBox } from './ScoreEntryForm';
import { getUniqueDisplayNames } from '../utils/playerDisplay';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * QuickCard - Group Voice Entry with Live Scorecard
 * 
 * Fast score entry for multiple players using voice input
 * with AI readback confirmation and traditional scorecard display.
 * 
 * Props:
 * - players: Array of { player_id, name, handicap }
 * - holes: 9 or 18
 * - startingNine: 'front' or 'back'
 * - holePars: Array of par values [4, 3, 5, ...]
 * - holeHandicaps: Array of hole handicap rankings [11, 17, 3, ...]
 * - courseName: String
 * - eventType: 'skins' | 'stroke_play'
 * - skinValue: Number (for skins games)
 * - onScoresUpdate: Callback when scores change
 * - onHoleComplete: Callback when a hole is confirmed
 * - initialScores: Object { player_id: [scores array] }
 * - initialHole: Starting hole number (for resuming mid-round)
 */

const QuickCard = ({
  players = [],
  holes = 9,
  startingNine = 'front',
  holePars = [],
  holeHandicaps = [],
  courseName = 'Course',
  eventType = 'skins',
  skinValue = 5,
  useHandicaps = false,
  onScoresUpdate,
  onHoleComplete,
  initialScores = {},
  initialHole = null, // For resuming mid-round
  enableVoiceReadback = true
}) => {
  // Generate unique display names for duplicate first names
  const displayNames = getUniqueDisplayNames(players);
  
  // Current hole being entered (1-indexed display number)
  // Use initialHole if provided (for resuming), otherwise default based on startingNine
  const [currentHole, setCurrentHole] = useState(() => {
    if (initialHole) return initialHole;
    return startingNine === 'back' ? 10 : 1;
  });
  
  // All scores: { player_id: [score1, score2, ...] }
  const [allScores, setAllScores] = useState(() => {
    const initial = {};
    players.forEach(p => {
      initial[p.player_id] = initialScores[p.player_id] || Array(holes).fill(null);
    });
    return initial;
  });
  
  // Pending scores for current hole (before confirmation)
  const [pendingScores, setPendingScores] = useState({});
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Readback state
  const [readbackText, setReadbackText] = useState('');
  const [isReadingBack, setIsReadingBack] = useState(false);
  const [voiceReadbackEnabled, setVoiceReadbackEnabled] = useState(enableVoiceReadback);
  
  // Confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Manual score entry state (which player is being edited)
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  
  // Calculate hole array index from display number
  const getHoleIndex = (holeNumber) => {
    if (startingNine === 'back' && holes === 9) {
      return holeNumber - 10; // Hole 10 -> index 0
    }
    return holeNumber - 1; // Hole 1 -> index 0
  };
  
  // Get display hole numbers array
  const getDisplayHoles = () => {
    if (startingNine === 'back' && holes === 9) {
      return [10, 11, 12, 13, 14, 15, 16, 17, 18];
    } else if (holes === 9) {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9];
    }
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  };
  
  const displayHoles = getDisplayHoles();
  const currentHoleIndex = getHoleIndex(currentHole);
  const currentPar = holePars[currentHole - 1] || 4;
  const currentHdcp = holeHandicaps[currentHole - 1] || '-';
  
  // Calculate if player gets a stroke on current hole
  const getStrokesForHole = (playerHandicap, holeHdcpRank) => {
    if (!useHandicaps || !hasHandicap(playerHandicap) || !holeHdcpRank) return 0;
    const minHandicap = handicapBaseline(players);
    const adjustedHdcp = handicapStrokes(playerHandicap, minHandicap);
    if (adjustedHdcp >= holeHdcpRank + 18) return 2;
    if (adjustedHdcp >= holeHdcpRank) return 1;
    return 0;
  };
  
  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : 'audio/ogg';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        await processVoiceRecording(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTranscript('');
      setPendingScores({});
      setShowConfirmation(false);
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Could not access microphone');
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  // Process voice recording with AI
  const processVoiceRecording = async (audioBlob) => {
    setIsProcessing(true);
    setTranscript('Processing...');
    
    try {
      let fileExt = 'webm';
      if (audioBlob.type.includes('mp4')) fileExt = 'mp4';
      else if (audioBlob.type.includes('ogg')) fileExt = 'ogg';
      
      const formData = new FormData();
      formData.append('file', audioBlob, `voice.${fileExt}`);
      formData.append('current_hole', String(currentHole));
      formData.append('current_hole_par', String(currentPar));
      formData.append('player_names', JSON.stringify(players.map(p => p.name)));
      formData.append('group_mode', 'true');
      
      // Use existing transcribe endpoint or new group endpoint
      const response = await axios.post(`${API}/voice/transcribe-group`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setTranscript(response.data.transcript);
        
        // Parse group scores: { player_name: score }
        if (response.data.player_scores) {
          const pending = {};
          const usedPlayerIds = new Set(); // Track already matched players
          
          for (const [name, score] of Object.entries(response.data.player_scores)) {
            const nameLower = name.toLowerCase().trim();
            
            // Try to find the best matching player not already matched
            let matchedPlayer = null;
            
            // Priority 0: Check for "#2", "#3" suffix (e.g., "Chad #2", "Chad DeSano #2")
            const numberMatch = nameLower.match(/(.+?)\s*#(\d+)$/);
            if (numberMatch) {
              const baseName = numberMatch[1].trim();
              const playerNumber = parseInt(numberMatch[2]) - 1; // Convert to 0-indexed
              
              // Find all players matching the base name
              const matchingPlayers = players.filter(p => {
                const playerFirstName = p.name.toLowerCase().split(' ')[0];
                const playerFullName = p.name.toLowerCase();
                return playerFullName.includes(baseName) || baseName.includes(playerFirstName) || playerFirstName === baseName;
              });
              
              // Select the Nth player (by their order in the players array)
              if (matchingPlayers.length > playerNumber && !usedPlayerIds.has(matchingPlayers[playerNumber].player_id)) {
                matchedPlayer = matchingPlayers[playerNumber];
              }
            }
            
            // Priority 1: Exact full name match
            if (!matchedPlayer) {
              matchedPlayer = players.find(p => 
                !usedPlayerIds.has(p.player_id) &&
                p.name.toLowerCase() === nameLower
              );
            }
            
            // Priority 2: First name + last initial match (for disambiguated names like "Chad S.")
            if (!matchedPlayer) {
              matchedPlayer = players.find(p => {
                if (usedPlayerIds.has(p.player_id)) return false;
                const displayName = displayNames[p.player_id]?.toLowerCase();
                return displayName && (displayName === nameLower || displayName.replace('.', '') === nameLower);
              });
            }
            
            // Priority 3: First name match (only if unique among remaining players)
            if (!matchedPlayer) {
              const firstNameMatches = players.filter(p => {
                if (usedPlayerIds.has(p.player_id)) return false;
                const firstName = p.name.toLowerCase().split(' ')[0];
                return firstName === nameLower || nameLower.includes(firstName) || firstName.includes(nameLower);
              });
              
              // Only match if exactly one player matches
              if (firstNameMatches.length === 1) {
                matchedPlayer = firstNameMatches[0];
              }
            }
            
            if (matchedPlayer && score >= 1 && score <= 15) {
              pending[matchedPlayer.player_id] = score;
              usedPlayerIds.add(matchedPlayer.player_id);
            }
          }
          setPendingScores(pending);
          
          // Generate readback
          if (Object.keys(pending).length > 0) {
            generateReadback(pending);
            setShowConfirmation(true);
          } else {
            // Provide helpful guidance using disambiguated names
            const nameExamples = players.slice(0, 2).map(p => displayNames[p.player_id]).join(', ');
            toast.warning(`Could not parse scores. Try: "${nameExamples} got a 4"`);
          }
        }
      } else {
        toast.error(response.data.message || 'Could not process voice');
      }
    } catch (error) {
      console.error('Voice processing error:', error);
      // Fallback: try the regular transcribe endpoint
      toast.error('Voice processing failed');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Generate readback text and optionally speak it
  const generateReadback = (pending) => {
    const parts = [];
    
    players.forEach(player => {
      const score = pending[player.player_id];
      if (score !== undefined) {
        const diff = score - currentPar;
        let term = '';
        if (diff === -2) term = 'eagle';
        else if (diff === -1) term = 'birdie';
        else if (diff === 0) term = 'par';
        else if (diff === 1) term = 'bogey';
        else if (diff === 2) term = 'double bogey';
        else if (diff > 2) term = `${diff} over`;
        else term = `${Math.abs(diff)} under`;
        
        // Use disambiguated display name
        const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
        parts.push(`${playerDisplayName}, ${term}`);
      }
    });
    
    const text = `Hole ${currentHole}. ${parts.join('. ')}. Is that correct?`;
    setReadbackText(text);
    
    // Speak it if enabled
    if (voiceReadbackEnabled) {
      speakReadback(text);
    }
  };
  
  // Text-to-speech readback
  const speakReadback = async (text) => {
    setIsReadingBack(true);
    try {
      // Use browser TTS for now (can upgrade to OpenAI TTS later)
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.1;
      utterance.onend = () => setIsReadingBack(false);
      utterance.onerror = () => setIsReadingBack(false);
      speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('TTS error:', error);
      setIsReadingBack(false);
    }
  };
  
  // Confirm scores and move to next hole
  const confirmScores = () => {
    const newAllScores = { ...allScores };
    
    players.forEach(player => {
      const score = pendingScores[player.player_id];
      if (score !== undefined) {
        const scores = [...(newAllScores[player.player_id] || Array(holes).fill(null))];
        scores[currentHoleIndex] = score;
        newAllScores[player.player_id] = scores;
      }
    });
    
    setAllScores(newAllScores);
    setPendingScores({});
    setShowConfirmation(false);
    setTranscript('');
    setReadbackText('');
    
    // Callback
    if (onHoleComplete) {
      onHoleComplete(currentHole, pendingScores);
    }
    if (onScoresUpdate) {
      onScoresUpdate(newAllScores);
    }
    
    // Auto-advance to next hole
    const maxHole = startingNine === 'back' ? 18 : holes;
    if (currentHole < maxHole) {
      setCurrentHole(currentHole + 1);
      toast.success(`Hole ${currentHole} recorded!`);
    } else {
      toast.success('All holes complete!');
    }
  };
  
  // Cancel/redo current entry
  const cancelEntry = () => {
    setPendingScores({});
    setShowConfirmation(false);
    setTranscript('');
    setReadbackText('');
    speechSynthesis.cancel();
  };
  
  // Manual score tap - now handled by ScoreBox component
  const handleManualScoreChange = (playerId, newScore) => {
    // Update pending scores for this player
    setPendingScores(prev => ({
      ...prev,
      [playerId]: parseInt(newScore) || null
    }));
    
    // Check if all players have scores entered
    const updatedPending = {
      ...pendingScores,
      [playerId]: parseInt(newScore) || null
    };
    
    const allEntered = players.every(p => updatedPending[p.player_id] !== undefined && updatedPending[p.player_id] !== null);
    if (allEntered) {
      setShowConfirmation(true);
      setReadbackText(`All scores entered for Hole ${currentHole}`);
    }
  };
  
  // Navigate holes - SAVE PENDING SCORES BEFORE NAVIGATING
  const prevHole = () => {
    const minHole = startingNine === 'back' ? 10 : 1;
    if (currentHole > minHole) {
      // Save any pending scores before navigating
      savePendingScoresToAllScores();
      setCurrentHole(currentHole - 1);
      setPendingScores({});
      setShowConfirmation(false);
    }
  };
  
  const nextHole = () => {
    const maxHole = startingNine === 'back' ? 18 : (holes === 18 ? 18 : 9);
    if (currentHole < maxHole) {
      // Save any pending scores before navigating
      savePendingScoresToAllScores();
      setCurrentHole(currentHole + 1);
      setPendingScores({});
      setShowConfirmation(false);
    }
  };
  
  // Helper to save pending scores without advancing hole
  const savePendingScoresToAllScores = () => {
    if (Object.keys(pendingScores).length === 0) return;
    
    const newAllScores = { ...allScores };
    let hasChanges = false;
    
    players.forEach(player => {
      const score = pendingScores[player.player_id];
      if (score !== undefined && score !== null) {
        const scores = [...(newAllScores[player.player_id] || Array(holes).fill(null))];
        scores[currentHoleIndex] = score;
        newAllScores[player.player_id] = scores;
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setAllScores(newAllScores);
      // Callback to parent
      if (onScoresUpdate) {
        onScoresUpdate(newAllScores);
      }
    }
  };
  
  // Calculate totals for a player
  const getPlayerTotal = (playerId) => {
    const scores = allScores[playerId] || [];
    return scores.reduce((sum, s) => sum + (s || 0), 0);
  };
  
  // Get score display style
  const getScoreStyle = (score, par) => {
    if (score === null || score === undefined) return 'bg-white text-stone-300 border-stone-200';
    const diff = score - (par || 4);
    if (diff <= -2) return 'bg-yellow-400 text-yellow-900 border-yellow-500'; // Eagle+
    if (diff === -1) return 'bg-green-500 text-white border-green-600'; // Birdie
    if (diff === 0) return 'bg-green-100 text-green-800 border-green-300'; // Par
    if (diff === 1) return 'bg-amber-100 text-amber-800 border-amber-300'; // Bogey
    if (diff === 2) return 'bg-red-100 text-red-800 border-red-300'; // Double
    return 'bg-red-200 text-red-900 border-red-400'; // Triple+
  };
  
  // Find best score on a hole (for skins)
  const getBestScoreOnHole = (holeIndex) => {
    let best = null;
    let bestPlayers = [];
    let competitors = 0;
    
    players.forEach(player => {
      const raw = allScores[player.player_id]?.[holeIndex];
      const handicap = eventHandicap(player);
      if (useHandicaps && !hasHandicap(handicap)) return;
      const score = raw == null ? raw : raw - (useHandicaps ? getStrokesForHole(handicap, holeHandicaps[holeIndex]) : 0);
      if (score !== null && score !== undefined) {
        competitors += 1;
        if (best === null || score < best) {
          best = score;
          bestPlayers = [player.player_id];
        } else if (score === best) {
          bestPlayers.push(player.player_id);
        }
      }
    });
    
    // Only one player = wins the skin
    return competitors >= 2 && bestPlayers.length === 1 ? bestPlayers[0] : null;
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="quickcard">
      {/* Header */}
      <div className="bg-green-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">QuickCard</h1>
            <p className="text-green-200 text-sm">{courseName}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-green-200">{eventType === 'skins' ? `$${skinValue} Skins` : 'Stroke Play'}</p>
            <p className="text-sm">{players.length} Players</p>
          </div>
        </div>
      </div>
      
      {/* Current Hole Voice Entry */}
      <div className="bg-white border-b p-4">
        {/* Hole Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={prevHole}
            className="p-2 rounded-full hover:bg-stone-100 disabled:opacity-30"
            disabled={currentHole <= (startingNine === 'back' ? 10 : 1)}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <p className="text-3xl font-black text-green-700">Hole {currentHole}</p>
            <p className="text-sm text-stone-500">
              Par {currentPar} | Hdcp {currentHdcp}
            </p>
            {startingNine === 'back' && holes === 9 && (
              <p className="text-xs text-amber-600">(Back 9)</p>
            )}
          </div>
          
          <button 
            onClick={nextHole}
            className="p-2 rounded-full hover:bg-stone-100 disabled:opacity-30"
            disabled={currentHole >= (startingNine === 'back' ? 18 : holes)}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        
        {/* Voice Record Button */}
        {!showConfirmation && (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-red-500 text-white animate-pulse scale-110' 
                  : isProcessing
                    ? 'bg-stone-300 text-stone-500'
                    : 'bg-green-600 text-white hover:bg-green-700 hover:scale-105'
              }`}
              data-testid="quickcard-mic-btn"
            >
              {isProcessing ? (
                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
              ) : isRecording ? (
                <MicOff className="w-8 h-8" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </button>
            
            <p className="text-sm text-stone-500 text-center">
              {isRecording ? 'Tap to stop' : isProcessing ? 'Processing...' : 'Tap to speak all scores'}
            </p>
            
            <p className="text-xs text-stone-400 text-center mt-1">
              Or enter scores manually below
            </p>
            
            {/* Manual Score Entry for Each Player */}
            <div className="w-full mt-4 space-y-3">
              <p className="text-xs text-stone-500 font-medium text-center">Tap a score to enter</p>
              <div className="grid grid-cols-2 gap-3">
                {players.map(player => {
                  const currentScore = pendingScores[player.player_id];
                  const par = holePars[currentHole - 1] || 4;
                  const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                  
                  return (
                    <div key={player.player_id} className="flex flex-col items-center gap-1">
                      <span className="text-xs text-stone-600 font-medium truncate max-w-full">
                        {playerDisplayName}
                      </span>
                      <div className="w-16">
                        <ScoreBox
                          value={currentScore !== undefined ? currentScore.toString() : ''}
                          onChange={(val) => handleManualScoreChange(player.player_id, val)}
                          holeNumber={currentHole}
                          par={par}
                          disabled={false}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {transcript && !showConfirmation && (
              <div className="bg-stone-100 rounded-lg p-3 w-full">
                <p className="text-xs text-stone-500">You said:</p>
                <p className="text-sm font-medium">&ldquo;{transcript}&rdquo;</p>
              </div>
            )}
          </div>
        )}
        
        {/* Confirmation Panel */}
        {showConfirmation && (
          <div className="space-y-4">
            {/* Parsed Scores */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-green-800">Hole {currentHole} Scores</span>
                <button
                  onClick={() => setVoiceReadbackEnabled(!voiceReadbackEnabled)}
                  className="p-1 rounded hover:bg-green-100"
                >
                  {voiceReadbackEnabled ? (
                    <Volume2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <VolumeX className="w-5 h-5 text-stone-400" />
                  )}
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {players.map(player => {
                  const score = pendingScores[player.player_id];
                  const hasScore = score !== undefined;
                  const diff = hasScore ? score - currentPar : 0;
                  const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                  
                  return (
                    <div 
                      key={player.player_id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        hasScore ? 'bg-white' : 'bg-stone-100'
                      }`}
                    >
                      <span className="font-medium text-sm truncate">
                        {playerDisplayName}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${
                          hasScore 
                            ? diff < 0 ? 'text-green-600' : diff === 0 ? 'text-blue-600' : 'text-amber-600'
                            : 'text-stone-300'
                        }`}>
                          {hasScore ? score : '-'}
                        </span>
                        {hasScore && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Readback Text */}
            {readbackText && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  {isReadingBack && '🔊 '}{readbackText}
                </p>
              </div>
            )}
            
            {/* Confirm/Redo Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={cancelEntry}
                variant="outline"
                className="flex-1 h-12"
                data-testid="quickcard-redo-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Redo
              </Button>
              <Button
                onClick={confirmScores}
                className="flex-1 h-12 bg-green-600 hover:bg-green-700"
                data-testid="quickcard-confirm-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Confirm & Next
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Live Scorecard */}
      <div className="p-3">
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="bg-stone-100 px-3 py-2 border-b">
            <span className="font-bold text-sm text-stone-700">Live Scorecard</span>
          </div>
          
          {/* Scrollable scorecard */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                {/* Hole numbers row */}
                <tr className="bg-stone-50">
                  <th className="sticky left-0 bg-stone-50 px-2 py-1 text-left font-bold text-stone-600 min-w-[70px]">
                    Hole
                  </th>
                  {displayHoles.map(hole => (
                    <th 
                      key={hole} 
                      className={`px-1 py-1 text-center font-bold min-w-[28px] ${
                        hole === currentHole ? 'bg-amber-100 text-amber-700' : 'text-stone-600'
                      }`}
                    >
                      {hole}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-center font-bold text-stone-600 min-w-[36px]">
                    {startingNine === 'back' || holes === 18 ? 'IN' : 'OUT'}
                  </th>
                </tr>
                
                {/* Par row */}
                <tr className="bg-stone-50 border-b">
                  <td className="sticky left-0 bg-stone-50 px-2 py-1 text-stone-500 font-medium">
                    Par
                  </td>
                  {displayHoles.map(hole => (
                    <td key={hole} className="px-1 py-1 text-center text-stone-500">
                      {holePars[hole - 1] || 4}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center text-stone-500 font-medium">
                    {displayHoles.reduce((sum, h) => sum + (holePars[h - 1] || 4), 0)}
                  </td>
                </tr>
                
                {/* Handicap row */}
                <tr className="border-b">
                  <td className="sticky left-0 bg-white px-2 py-1 text-stone-400 text-[10px]">
                    Hdcp
                  </td>
                  {displayHoles.map(hole => (
                    <td key={hole} className="px-1 py-1 text-center text-stone-400 text-[10px]">
                      {holeHandicaps[hole - 1] || '-'}
                    </td>
                  ))}
                  <td></td>
                </tr>
              </thead>
              
              <tbody>
                {players.map((player, pIdx) => {
                  const scores = allScores[player.player_id] || [];
                  const total = getPlayerTotal(player.player_id);
                  const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                  
                  return (
                    <tr key={player.player_id} className={pIdx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      {/* Player name + handicap */}
                      <td className={`sticky left-0 ${pIdx % 2 === 0 ? 'bg-white' : 'bg-stone-50'} px-2 py-2 border-r`}>
                        <div className="font-medium text-stone-800 truncate max-w-[60px]">
                          {playerDisplayName}
                        </div>
                        {(
                          <div className="text-[10px] text-stone-400">
                            {handicapLabel(eventHandicap(player))}
                          </div>
                        )}
                      </td>
                      
                      {/* Scores */}
                      {displayHoles.map((hole, hIdx) => {
                        const score = scores[hIdx];
                        const pending = pendingScores[player.player_id];
                        const isPending = hole === currentHole && pending !== undefined;
                        const displayScore = isPending ? pending : score;
                        const par = holePars[hole - 1] || 4;
                        const isBest = getBestScoreOnHole(hIdx) === player.player_id;
                        const getsStroke = getStrokesForHole(eventHandicap(player), holeHandicaps[hole - 1]) > 0;
                        
                        return (
                          <td 
                            key={hole} 
                            className={`px-1 py-1 text-center ${
                              hole === currentHole ? 'bg-amber-50' : ''
                            }`}
                          >
                            <div className={`relative w-6 h-6 mx-auto rounded flex items-center justify-center text-xs font-bold border ${
                              isPending 
                                ? 'border-amber-400 bg-amber-100 text-amber-700'
                                : getScoreStyle(displayScore, par)
                            } ${isBest ? 'ring-2 ring-green-500' : ''}`}>
                              {displayScore !== null && displayScore !== undefined ? displayScore : '-'}
                              {getsStroke && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                              )}
                            </div>
                          </td>
                        );
                      })}
                      
                      {/* Total */}
                      <td className="px-2 py-1 text-center font-bold text-stone-800">
                        {total || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Legend */}
          <div className="px-3 py-2 bg-stone-50 border-t text-[10px] text-stone-500 flex flex-wrap gap-3">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-500 rounded" /> Birdie
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-100 border border-green-300 rounded" /> Par
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded" /> Bogey
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded-full" /> Stroke
            </span>
            {eventType === 'skins' && (
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 border-2 border-green-500 rounded" /> Skin
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Skins Summary (if applicable) */}
      {eventType === 'skins' && (
        <div className="px-3 pb-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-amber-800">Skins</span>
              <span className="text-sm text-amber-600">${skinValue}/skin</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {players.map(player => {
                const skinsWon = displayHoles.filter((_, hIdx) => 
                  getBestScoreOnHole(hIdx) === player.player_id
                ).length;
                const playerDisplayName = displayNames[player.player_id] || player.name.split(' ')[0];
                
                return (
                  <div 
                    key={player.player_id}
                    className="bg-white px-2 py-1 rounded-lg text-sm"
                  >
                    <span className="font-medium">{playerDisplayName}</span>
                    <span className="text-amber-600 ml-1">{skinsWon}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickCard;
